import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { EmptyState } from '../patterns/EmptyState.js';
import { Skeleton } from './Skeleton.js';
import { TABLE_PATTERN_MARKERS, type TablePatternId } from '../tables/catalog.js';
import { controlFocus } from '../primitives/shared.js';

export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  sortable?: boolean;
  frozen?: boolean;
  numeric?: boolean;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  /** Names the table. 07-11 PRM-23 requires a caption or accessible name. */
  caption: string;
  emptyMessage?: string;
  loading?: boolean;
  error?: string;
  onRetry?: () => void;
  disabled?: boolean;
  pattern?: TablePatternId;
  sortKey?: string;
  sortDirection?: 'ascending' | 'descending' | 'none';
  onSort?: (key: string) => void;
  rowKey?: (row: T, index: number) => string;
  selectedKeys?: string[];
  onRowActivate?: (row: T) => void;
  className?: string;
}

/**
 * PRM-23 Table — sortable, selectable, sticky header. Reflow (1.4.10) permits
 * horizontal scrolling for a data table, which is why the wrapper scrolls
 * rather than the columns collapsing. Not permitted in `floor`.
 */
export function DataTable<T>({
  columns,
  rows,
  caption,
  emptyMessage = 'No rows',
  loading,
  error,
  onRetry,
  disabled,
  pattern = 'standard',
  sortKey,
  sortDirection = 'none',
  onSort,
  rowKey,
  selectedKeys,
  onRowActivate,
  className,
}: DataTableProps<T>) {
  const marker = TABLE_PATTERN_MARKERS[pattern];
  const selected = new Set(selectedKeys ?? []);
  const [announcement, setAnnouncement] = useState('');
  const lastCount = useRef<number>(-1);

  useEffect(() => {
    if (selectedKeys && selectedKeys.length !== lastCount.current) {
      lastCount.current = selectedKeys.length;
      setAnnouncement(`${selectedKeys.length} of ${rows.length} rows selected`);
    }
  }, [selectedKeys, rows.length]);

  if (loading) {
    return (
      <div data-table-pattern={marker} role="status" aria-live="polite">
        <span className="sr-only">Loading {caption}</span>
        <Skeleton lines={5} />
      </div>
    );
  }

  if (error) {
    return (
      <div data-table-pattern={marker}>
        <EmptyState
          variant="loadFailed"
          title={error}
          description="Retry, or contact support if it keeps failing."
          actionLabel={onRetry ? 'Retry' : undefined}
          onAction={onRetry}
        />
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div data-table-pattern={marker}>
        <EmptyState
          variant="empty"
          title={emptyMessage}
          description="Adjust filters or create the first record."
        />
      </div>
    );
  }

  return (
    <div
      className={cn('w-full overflow-x-auto', disabled && 'opacity-60', className)}
      aria-disabled={disabled || undefined}
    >
      <table
        data-primitive="PRM-23"
        data-table-pattern={marker}
        className="w-full border-collapse text-table"
      >
        <caption className="sr-only">{caption}</caption>
        <thead className="sticky top-0 z-[var(--z-sticky)] bg-raised">
          <tr>
            {columns.map((c) => {
              const isSorted = sortKey === c.key;
              const ariaSort = c.sortable ? (isSorted ? sortDirection : 'none') : undefined;

              return (
                <th
                  key={c.key}
                  scope="col"
                  aria-sort={ariaSort}
                  className={cn(
                    'border-b border-line bg-raised p-row-padding text-left font-medium',
                    c.numeric && 'tabular text-right',
                    c.frozen && 'sticky left-0 z-[var(--z-sticky)]',
                  )}
                >
                  {c.sortable && onSort ? (
                    <button
                      type="button"
                      onClick={() => onSort(c.key)}
                      className={cn(
                        'inline-flex items-center gap-xs font-inherit',
                        'hov:text-link',
                        controlFocus,
                      )}
                    >
                      {c.header}
                      <span aria-hidden="true">
                        {isSorted ? (sortDirection === 'ascending' ? '▲' : '▼') : '↕'}
                      </span>
                    </button>
                  ) : (
                    c.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const key = rowKey ? rowKey(row, i) : String(i);
            const isSelected = selected.has(key);
            return (
              <tr
                key={key}
                aria-selected={selectedKeys ? isSelected : undefined}
                tabIndex={onRowActivate ? 0 : undefined}
                onClick={onRowActivate && !disabled ? () => onRowActivate(row) : undefined}
                onKeyDown={
                  onRowActivate && !disabled
                    ? (e) => {
                        if (e.key === 'Enter') onRowActivate(row);
                      }
                    : undefined
                }
                className={cn(
                  i % 2 ? 'bg-row-alt' : undefined,
                  isSelected && 'bg-selected',
                  onRowActivate && !disabled && 'cursor-pointer hov:bg-sunken',
                  onRowActivate && controlFocus,
                )}
              >
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={cn(
                      'border-b border-line-subtle p-row-padding',
                      c.numeric && 'tabular text-right',
                      c.frozen && 'sticky left-0 bg-raised',
                    )}
                  >
                    {c.render(row)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      <span aria-live="polite" className="sr-only">
        {announcement}
      </span>
    </div>
  );
}
